import { Box, Button, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Switch, TextField } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { Role } from '../../Models/Role/Role.model';
import { RootState } from '../../reducers';
import {
    fetchAnalyticsWidgets,
    fetchAssessmentsByRole,
    fetchFacilities,
    fetchFacilityZipCoeds,
    fetchHedisMetricsWidgets,
    fetchRoles,
    fetchSmartDashboardWidgets,
    getInitialValues,
    openNewHandler,
    setAssignedAssessments,
    setAssignedFacilities,
    setAssignedWidgets,
    setRoleName,
    setRolePermissions,
    setRoles,
    setSelectedRole,
    setSelectedTab,
} from '../../store/adminRoles.slice';
import { getTCCRoleProviders } from '../../store/provider.slice';
import { fetchPermissions } from '../../store/shared.slice';
import Loading from '../Shared/Loading';
import MessageDisplay from '../Shared/MessageDisplay';
import AssessmentsByRole from './AssessmentsByRole';
import Facility from './Facilities';
import Permissions from './Permissions';
import Widgets from './Widgets';

const AdminRoles = (props) => {
    const tccRoleName = 'Transitional Care Coordinator';
    const dispatch = useAppDispatch();
    const { openNew, selectedRole, roles, roleName, selectedTab } = useSelector((state: RootState) => state.adminRoles);

    const tabChangeHandler = (e: React.ChangeEvent<{}>, newValue: number) => {
        dispatch(setSelectedTab(newValue));
    };

    const handleChange = (e: SelectChangeEvent<Role>) => {
        const role = roles.find((role) => role === e.target.value);
        dispatch(setSelectedRole(role));
        dispatch(setRoleName(role.Name ? role.Name : ''));
        dispatch(setRolePermissions(role.Permissions ? role.Permissions : []));
        dispatch(setAssignedWidgets(role.Widgets ? role.Widgets : []));
        dispatch(setAssignedFacilities(role.ProviderFacilities ? role.ProviderFacilities : []));
        const orderedList = role.AssignedAssessments ? [...role.AssignedAssessments].sort((a, b) => (a.Order || 0) - (b.Order || 0)) : [];
        dispatch(setAssignedAssessments(orderedList));
    };

    useEffect(() => {
        dispatch(getTCCRoleProviders(false));
        dispatch(fetchAssessmentsByRole());
        dispatch(fetchRoles());
        dispatch(fetchPermissions());
        dispatch(fetchAnalyticsWidgets());
        dispatch(fetchSmartDashboardWidgets());
        dispatch(fetchHedisMetricsWidgets());
        dispatch(fetchFacilities());
        dispatch(fetchFacilityZipCoeds());
        dispatch(getInitialValues());
        document.title = props.title;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box>
            <Loading message={'Loading...'} />
            <MessageDisplay />
            <Grid container justifyContent={'flex-end'} spacing={3} style={{ padding: '16px 16px 16px 16px' }}>
                <Grid size={12} marginTop={2} p={2} display="flex" justifyContent="space-between">
                    <FormControl className="form-control-200">
                        <InputLabel id="demo-simple-select-label">Select Role</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={roles.find((role) => role === selectedRole) || '-1'}
                            label="Select Role"
                            disabled={openNew ? true : false}
                            onChange={handleChange}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                        >
                            <MenuItem value={'-1'} disabled>
                                Select Role
                            </MenuItem>
                            {roles.map((role) => {
                                return (
                                    <MenuItem key={role.Id} value={role}>
                                        {`${role.Name}`}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <Button variant="contained" className="button-120" onClick={() => dispatch(openNewHandler())}>
                        Add Role
                    </Button>
                </Grid>
                {(selectedRole.Name.length > 0 || (selectedRole.Name.length === 0 && openNew)) && (
                    <>
                        <Grid size={12}>
                            <TextField
                                sx={{ minWidth: '300px' }}
                                required
                                value={roleName}
                                id="name"
                                label="Edit Role Name"
                                name="Name"
                                placeholder="Add Role Name"
                                variant="outlined"
                                onChange={(e) => dispatch(setRoleName(e.target.value))}
                            />
                        </Grid>
                        <Grid size={12}>
                            <FormControlLabel
                                label={'Allow assigning role to care teams'}
                                control={
                                    <Switch
                                        id={selectedRole.Id + '-switch'}
                                        checked={selectedRole.IsCareTeamRole}
                                        onChange={(e) => {
                                            let selectedRoleCopy = structuredClone(selectedRole);
                                            let newRoles = structuredClone(roles);

                                            selectedRoleCopy.IsCareTeamRole = e.target.checked;
                                            const roleIndex = newRoles.findIndex((role) => role.Id === selectedRoleCopy.Id);
                                            if (roleIndex !== -1) {
                                                newRoles[roleIndex] = selectedRoleCopy;
                                            }
                                            dispatch(setSelectedRole(selectedRoleCopy));
                                            dispatch(setRoles(newRoles));
                                        }}
                                    />
                                }
                            />
                        </Grid>
                    </>
                )}
                <Grid size={12}>
                    <Tabs variant="fullWidth" value={selectedTab} onChange={tabChangeHandler}>
                        <Tab value={0} label="Permissions" />
                        <Tab value={1} label="Smart Dashboard" />
                        <Tab value={2} label="Assessments" />
                        {roleName === tccRoleName ? <Tab value={3} label="Facilities" /> : null}
                    </Tabs>
                </Grid>

                {selectedTab === 0 && <Permissions />}
                {selectedTab === 1 && <Widgets />}
                {selectedTab === 2 && <AssessmentsByRole />}
                {selectedTab === 3 && <Facility />}
            </Grid>
        </Box>
    );
};

export default AdminRoles;
